/**
 * Learn Progressions Page
 *
 * Comprehensive guide to understanding and training chord progression recognition.
 * Covers theory, practical applications, and how to use the Progressions Drill tool.
 */

import React from "react";
import { Link } from "react-router-dom";
import {
    LearnPageLayout,
    ContentSection,
    CalloutBox,
    OutcomeItem,
} from "../../components/learn";
import { ROUTES } from "../../config/routes";
import { SEO_METADATA } from "../../config/seoMetadata";
import SEO from "../../components/SEO";
import styles from "./StartHere.module.css";

const tocItems = [
    { id: "what-are-progressions", label: "What are progressions?" },
    { id: "why-progressions-matter", label: "Why progressions matter" },
    { id: "roman-numerals", label: "Roman numeral analysis" },
    { id: "common-progressions", label: "Common progressions" },
    { id: "diatonic-chords", label: "Diatonic chords" },
    { id: "recognizing-progressions", label: "Recognizing progressions" },
    { id: "using-the-drill", label: "Using the progressions drill" },
    { id: "practice-strategies", label: "Practice strategies" },
];

export default function ProgressionsLearn() {
    return (
        <>
            <SEO {...SEO_METADATA.LEARN_PROGRESSIONS} />
            <LearnPageLayout
                title="Learn Chord Progressions"
                subtitle="Master the language of harmony and musical movement"
                tocItems={tocItems}
        >
            {/* What are Progressions */}
            <ContentSection id="what-are-progressions">
                <h2 className={styles.sectionTitle}>What are chord progressions?</h2>

                <p>
                    A <strong>chord progression</strong> is a sequence of chords played one after
                    another. While individual chords create mood, progressions create{" "}
                    <em>movement</em> and <em>narrative</em>. They take you on a journey—building
                    tension, releasing it, surprising you, or bringing you home.
                </p>

                <p>
                    Think of chord progressions as the sentences of music. Just like words have
                    more meaning when arranged in sequence, chords tell a story when they flow
                    from one to another. Every song you've ever loved is built on chord
                    progressions.
                </p>

                <CalloutBox>
                    <p>
                        <strong>Amazing fact:</strong> The vast majority of pop, rock, and country
                        songs use the same handful of chord progressions! Once you can recognize
                        these patterns, you'll start hearing them everywhere—and you'll be able to
                        play along with countless songs.
                    </p>
                </CalloutBox>

                <p>
                    Progressions are typically described using <strong>Roman numerals</strong>{" "}
                    (I, IV, V, etc.) rather than specific chord names (C, F, G). This is because
                    the same progression pattern works in any key—the relationships between
                    chords are what matter, not the absolute pitches.
                </p>
            </ContentSection>

            {/* Why Progressions Matter */}
            <ContentSection id="why-progressions-matter">
                <h2 className={styles.sectionTitle}>Why progressions matter</h2>

                <p>
                    Understanding chord progressions is the key that unlocks music. When you can
                    recognize progressions by ear, you gain:
                </p>

                <div style={{ marginTop: "1.5rem" }}>
                    <OutcomeItem icon="🎸" title="Play any song in any key">
                        Recognize that a song uses I-V-vi-IV, and you can play it in C, G, D, or
                        any key you want.
                    </OutcomeItem>
                    <OutcomeItem icon="🎵" title="Write songs faster">
                        Know which progressions create which emotions, and use proven patterns to
                        craft your own music.
                    </OutcomeItem>
                    <OutcomeItem icon="🎹" title="Improvise and solo confidently">
                        When you know what chord is coming next, you know which notes will sound
                        good in your solo.
                    </OutcomeItem>
                    <OutcomeItem icon="🎼" title="Understand music theory in context">
                        Progressions reveal how harmony actually works in real songs, not just in
                        textbooks.
                    </OutcomeItem>
                    <OutcomeItem icon="🔄" title="Transpose songs instantly">
                        Sing too high? Recognize the progression and move it to a more comfortable
                        key without thinking.
                    </OutcomeItem>
                </div>
            </ContentSection>

            {/* Roman Numeral Analysis */}
            <ContentSection id="roman-numerals">
                <h2 className={styles.sectionTitle}>Understanding Roman numeral analysis</h2>

                <p>
                    Musicians use Roman numerals to describe chord progressions because it makes
                    them universal. Let's break down this system:
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>Scale degrees</h3>
                <p>
                    In any major key, there are seven notes (called <strong>scale degrees</strong>
                    ). Each scale degree has a Roman numeral and a traditional name:
                </p>

                <div className={styles.progressionTable}>
                    <table style={{ width: "100%", marginTop: "1rem", borderCollapse: "collapse" }}>
                        <thead>
                            <tr style={{ borderBottom: "2px solid var(--text-color)", textAlign: "left" }}>
                                <th style={{ padding: "0.75rem" }}>Degree</th>
                                <th style={{ padding: "0.75rem" }}>Numeral</th>
                                <th style={{ padding: "0.75rem" }}>Traditional Name</th>
                                <th style={{ padding: "0.75rem" }}>In C Major</th>
                                <th style={{ padding: "0.75rem" }}>Function</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr style={{ borderBottom: "1px solid var(--border-color)" }}>
                                <td style={{ padding: "0.75rem" }}>1</td>
                                <td style={{ padding: "0.75rem" }}><strong>I</strong></td>
                                <td style={{ padding: "0.75rem" }}>Tonic</td>
                                <td style={{ padding: "0.75rem" }}>C Major</td>
                                <td style={{ padding: "0.75rem" }}>Home, stable, resolution</td>
                            </tr>
                            <tr style={{ borderBottom: "1px solid var(--border-color)" }}>
                                <td style={{ padding: "0.75rem" }}>2</td>
                                <td style={{ padding: "0.75rem" }}><strong>ii</strong></td>
                                <td style={{ padding: "0.75rem" }}>Supertonic</td>
                                <td style={{ padding: "0.75rem" }}>D minor</td>
                                <td style={{ padding: "0.75rem" }}>Leads to V, pre-dominant</td>
                            </tr>
                            <tr style={{ borderBottom: "1px solid var(--border-color)" }}>
                                <td style={{ padding: "0.75rem" }}>3</td>
                                <td style={{ padding: "0.75rem" }}><strong>iii</strong></td>
                                <td style={{ padding: "0.75rem" }}>Mediant</td>
                                <td style={{ padding: "0.75rem" }}>E minor</td>
                                <td style={{ padding: "0.75rem" }}>Bridges tonic and dominant</td>
                            </tr>
                            <tr style={{ borderBottom: "1px solid var(--border-color)" }}>
                                <td style={{ padding: "0.75rem" }}>4</td>
                                <td style={{ padding: "0.75rem" }}><strong>IV</strong></td>
                                <td style={{ padding: "0.75rem" }}>Subdominant</td>
                                <td style={{ padding: "0.75rem" }}>F Major</td>
                                <td style={{ padding: "0.75rem" }}>Creates tension, yearning</td>
                            </tr>
                            <tr style={{ borderBottom: "1px solid var(--border-color)" }}>
                                <td style={{ padding: "0.75rem" }}>5</td>
                                <td style={{ padding: "0.75rem" }}><strong>V</strong></td>
                                <td style={{ padding: "0.75rem" }}>Dominant</td>
                                <td style={{ padding: "0.75rem" }}>G Major</td>
                                <td style={{ padding: "0.75rem" }}>Strong tension, wants to resolve to I</td>
                            </tr>
                            <tr style={{ borderBottom: "1px solid var(--border-color)" }}>
                                <td style={{ padding: "0.75rem" }}>6</td>
                                <td style={{ padding: "0.75rem" }}><strong>vi</strong></td>
                                <td style={{ padding: "0.75rem" }}>Submediant</td>
                                <td style={{ padding: "0.75rem" }}>A minor</td>
                                <td style={{ padding: "0.75rem" }}>Relative minor, deceptive</td>
                            </tr>
                            <tr>
                                <td style={{ padding: "0.75rem" }}>7</td>
                                <td style={{ padding: "0.75rem" }}><strong>vii°</strong></td>
                                <td style={{ padding: "0.75rem" }}>Leading Tone</td>
                                <td style={{ padding: "0.75rem" }}>B diminished</td>
                                <td style={{ padding: "0.75rem" }}>Very unstable, rare in pop</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>Upper vs. lowercase</h3>
                <ul style={{ marginLeft: "1.5rem", lineHeight: "1.8" }}>
                    <li>
                        <strong>Uppercase</strong> (I, IV, V) = Major chords
                    </li>
                    <li>
                        <strong>Lowercase</strong> (ii, iii, vi) = Minor chords
                    </li>
                    <li>
                        <strong>Lowercase with °</strong> (vii°) = Diminished chord
                    </li>
                </ul>

                <CalloutBox>
                    <p>
                        <strong>Key insight:</strong> In a major key, the diatonic chords follow a
                        pattern: Major-minor-minor-Major-Major-minor-diminished (I-ii-iii-IV-V-vi-vii°).
                        This pattern is the same in every major key!
                    </p>
                </CalloutBox>
            </ContentSection>

            {/* Common Progressions */}
            <ContentSection id="common-progressions">
                <h2 className={styles.sectionTitle}>Common chord progressions</h2>

                <p>
                    Let's look at the most popular progressions in Western music. Once you learn
                    these, you'll recognize them in hundreds of songs:
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>I-V-vi-IV: The pop progression</h3>
                <p>
                    <strong>Examples:</strong> "Let It Be" (Beatles), "Someone Like You" (Adele),
                    "Don't Stop Believin'" (Journey), "Forever Young" (Alphaville)
                </p>
                <p>
                    This is arguably the most famous progression in modern pop music. It's
                    everywhere. Start with home (I), move to tension (V), go to the relative minor
                    for emotion (vi), then the subdominant (IV) before cycling back.
                </p>
                <p style={{ fontStyle: "italic", color: "var(--text-secondary)" }}>
                    In C Major: C → G → Am → F
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>I-IV-V: The rock progression</h3>
                <p>
                    <strong>Examples:</strong> "Twist and Shout" (Beatles), "La Bamba" (Ritchie
                    Valens), "Wild Thing" (The Troggs)
                </p>
                <p>
                    The classic three-chord rock song. Simple, powerful, and timeless. Tonic,
                    subdominant, dominant—the three pillars of harmony.
                </p>
                <p style={{ fontStyle: "italic", color: "var(--text-secondary)" }}>
                    In C Major: C → F → G
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>vi-IV-I-V: The sensitive progression</h3>
                <p>
                    <strong>Examples:</strong> "Africa" (Toto), "Poker Face" (Lady Gaga), "Apologize" (OneRepublic)
                </p>
                <p>
                    Start with the minor vi chord for an emotional, introspective beginning, then
                    resolve through IV-I-V. Very popular in 2000s pop.
                </p>
                <p style={{ fontStyle: "italic", color: "var(--text-secondary)" }}>
                    In C Major: Am → F → C → G
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>I-vi-IV-V: The '50s progression</h3>
                <p>
                    <strong>Examples:</strong> "Stand By Me" (Ben E. King), "Every Breath You Take"
                    (The Police), "Blue Moon" (countless versions)
                </p>
                <p>
                    Classic doo-wop and early rock & roll progression. Nostalgic and smooth.
                </p>
                <p style={{ fontStyle: "italic", color: "var(--text-secondary)" }}>
                    In C Major: C → Am → F → G
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>ii-V-I: The jazz turnaround</h3>
                <p>
                    <strong>Examples:</strong> Found in virtually every jazz standard
                </p>
                <p>
                    The fundamental building block of jazz harmony. Creates smooth voice leading
                    and strong resolution. Often extended with 7th chords (iim7-V7-Imaj7).
                </p>
                <p style={{ fontStyle: "italic", color: "var(--text-secondary)" }}>
                    In C Major: Dm → G → C
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>I-V-vi-iii-IV-I-IV-V: The extended pop progression</h3>
                <p>
                    <strong>Examples:</strong> "Let It Be" (Beatles - extended version)
                </p>
                <p>
                    A longer journey through the key, visiting more chords before returning home.
                    Creates a more elaborate harmonic story.
                </p>
                <p style={{ fontStyle: "italic", color: "var(--text-secondary)" }}>
                    In C Major: C → G → Am → Em → F → C → F → G
                </p>

                <CalloutBox>
                    <p>
                        <strong>Try this exercise:</strong> Pick a progression above and try to
                        sing or play it in different keys. Notice how the <em>feeling</em> of the
                        progression stays the same even though the actual notes change. That's the
                        power of Roman numeral thinking!
                    </p>
                </CalloutBox>
            </ContentSection>

            {/* Diatonic Chords */}
            <ContentSection id="diatonic-chords">
                <h2 className={styles.sectionTitle}>Understanding diatonic chords</h2>

                <p>
                    <strong>Diatonic chords</strong> are the chords that naturally occur in a key.
                    They're built by stacking thirds on each scale degree, using only the notes
                    from that key's scale.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>The three functional groups</h3>
                <p>
                    Diatonic chords are often grouped by their harmonic function:
                </p>

                <div style={{ marginTop: "1.5rem", marginLeft: "1.5rem" }}>
                    <h4><strong>Tonic family (stable, home):</strong> I, iii, vi</h4>
                    <p style={{ marginLeft: "1rem", lineHeight: "1.8" }}>
                        These chords feel like home or resolution. The I chord is the strongest
                        tonic, while iii and vi share similar qualities.
                    </p>

                    <h4 style={{ marginTop: "1.5rem" }}><strong>Subdominant family (preparation):</strong> ii, IV</h4>
                    <p style={{ marginLeft: "1rem", lineHeight: "1.8" }}>
                        These chords create a sense of departure or preparation. They often lead to
                        dominant chords.
                    </p>

                    <h4 style={{ marginTop: "1.5rem" }}><strong>Dominant family (tension):</strong> V, vii°</h4>
                    <p style={{ marginLeft: "1rem", lineHeight: "1.8" }}>
                        These chords create tension that wants to resolve back to tonic. The V
                        chord especially has a powerful pull toward I.
                    </p>
                </div>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>Why these groupings matter</h3>
                <p>
                    Understanding functional harmony helps you predict what chord might come next:
                </p>
                <ul style={{ marginLeft: "1.5rem", lineHeight: "1.8" }}>
                    <li>Tonic chords often move to subdominant or dominant</li>
                    <li>Subdominant chords typically move to dominant</li>
                    <li>Dominant chords strongly want to resolve to tonic</li>
                </ul>
                <p>
                    But in modern music, composers break these "rules" all the time to create
                    interesting effects! The I-V-vi-IV progression, for example, has an unexpected
                    move from V (dominant) to vi (tonic family) instead of resolving to I.
                </p>
            </ContentSection>

            {/* Recognizing Progressions */}
            <ContentSection id="recognizing-progressions">
                <h2 className={styles.sectionTitle}>How to recognize progressions</h2>

                <p>
                    Recognizing progressions is like recognizing sentences in a language you're
                    learning. Here's how to develop this skill:
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>1. Start with the bass note</h3>
                <p>
                    The bass note (lowest note) is usually the root of the chord. Train yourself
                    to follow the bass line—it tells you which scale degrees are being used. Sing
                    or hum along with the bass to internalize the movement.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>2. Listen for functional movement</h3>
                <p>Ask yourself:</p>
                <ul style={{ marginLeft: "1.5rem", lineHeight: "1.8" }}>
                    <li>Does this chord feel like <em>home</em>? (probably I)</li>
                    <li>Does it feel <em>tense</em> and want to resolve? (probably V)</li>
                    <li>Does it feel like <em>preparation</em> for something? (probably IV or ii)</li>
                    <li>Does it feel <em>sad but stable</em>? (probably vi)</li>
                </ul>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>3. Identify the tonic (I) chord first</h3>
                <p>
                    Most songs start or end on the I chord. Once you find the home chord, you have
                    your reference point. All other chords are heard in relation to that tonic.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>4. Listen for strong resolutions</h3>
                <p>
                    The V-to-I resolution is the strongest movement in Western harmony. When you
                    hear a chord that feels like it's being "pulled" to the next chord, that's
                    likely a V-I movement. This helps you identify both the V and I chords.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>5. Recognize common patterns</h3>
                <p>
                    Once you've heard I-V-vi-IV a few dozen times, you'll start recognizing it
                    instantly in new songs. Build up your library of familiar progressions through
                    repeated exposure.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>6. Sing scale degrees</h3>
                <p>
                    Advanced technique: Try to sing the root notes of each chord using solfège (do,
                    re, mi, etc.) or numbers (1, 2, 3, etc.). This directly connects what you hear
                    to the Roman numerals.
                </p>

                <CalloutBox>
                    <p>
                        <strong>Important prerequisite:</strong> You'll have an much easier time
                        recognizing progressions if you can already distinguish major from minor
                        chords. If you're not confident with that skill yet, spend time with the{" "}
                        <Link to={ROUTES.DRILL.CHORDS} style={{ color: "var(--accent-color)", textDecoration: "underline" }}>
                            Chords Drill
                        </Link>{" "}
                        first!
                    </p>
                </CalloutBox>
            </ContentSection>

            {/* Using the Drill */}
            <ContentSection id="using-the-drill">
                <h2 className={styles.sectionTitle}>Using the progressions drill</h2>

                <p>
                    Ready to train your ear for progressions? The{" "}
                    <Link to={ROUTES.DRILL.PROGRESSIONS} style={{ color: "var(--accent-color)", textDecoration: "underline" }}>
                        Progressions Drill
                    </Link>{" "}
                    helps you recognize chord sequences in context.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>How the drill works</h3>
                <ol style={{ marginLeft: "1.5rem", lineHeight: "1.8" }}>
                    <li>
                        <strong>Select your settings:</strong>
                        <ul style={{ marginLeft: "1.5rem", marginTop: "0.5rem" }}>
                            <li>
                                <strong>Diatonic chords:</strong> Choose which scale degrees to
                                include (I, ii, iii, IV, V, vi, vii°). Start with I, IV, V, and vi.
                            </li>
                            <li>
                                <strong>Progression length:</strong> 3 or 4 chords. Start with 3 for
                                easier recognition.
                            </li>
                            <li>
                                <strong>Key:</strong> Choose the key the progressions will be played
                                in.
                            </li>
                            <li>
                                <strong>Difficulty:</strong> Easy (very different answer options),
                                Medium (similar options), or Hard (very similar options).
                            </li>
                        </ul>
                    </li>
                    <li>
                        <strong>Choose a practice mode:</strong>
                        <ul style={{ marginLeft: "1.5rem", marginTop: "0.5rem" }}>
                            <li><strong>Questions mode:</strong> Fixed number of progressions</li>
                            <li><strong>Time mode:</strong> Practice for a set duration</li>
                            <li><strong>Endless mode:</strong> Continuous practice</li>
                        </ul>
                    </li>
                    <li>
                        <strong>Start the drill:</strong> You'll hear a progression played (3 or 4
                        chords in sequence).
                    </li>
                    <li>
                        <strong>Identify the progression:</strong> Select which Roman numeral
                        sequence you heard from the multiple-choice options.
                    </li>
                    <li>
                        <strong>Get feedback:</strong> Instantly see if you're correct, and try again
                        if not.
                    </li>
                    <li>
                        <strong>Review results:</strong> Track your accuracy and progress.
                    </li>
                </ol>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>Playback options</h3>
                <ul style={{ marginLeft: "1.5rem", lineHeight: "1.8" }}>
                    <li>
                        <strong>Instrument:</strong> Choose different timbres to hear progressions in
                        various contexts.
                    </li>
                    <li>
                        <strong>Volume:</strong> Adjust to your preference.
                    </li>
                    <li>
                        <strong>Replay:</strong> Listen to the progression as many times as needed.
                    </li>
                    <li>
                        <strong>Reference note:</strong> Play the tonic (I) note to remind yourself
                        of the home pitch.
                    </li>
                </ul>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>Difficulty levels explained</h3>
                <p>
                    The difficulty setting determines how similar the wrong answer options are to
                    the correct progression:
                </p>
                <ul style={{ marginLeft: "1.5rem", lineHeight: "1.8" }}>
                    <li>
                        <strong>Easy:</strong> Wrong answers are very different (e.g., if the correct
                        answer is I-IV-V, wrong answers might be completely different patterns).
                    </li>
                    <li>
                        <strong>Medium:</strong> Wrong answers share some chords with the correct
                        answer (e.g., I-IV-V vs. I-IV-vi).
                    </li>
                    <li>
                        <strong>Hard:</strong> Wrong answers might differ by only one chord or the
                        order of chords (e.g., I-IV-V-I vs. I-V-IV-I).
                    </li>
                </ul>

                <CalloutBox>
                    <p>
                        <strong>Ready to start?</strong> Head to the{" "}
                        <Link to={ROUTES.DRILL.PROGRESSIONS} style={{ color: "var(--accent-color)", textDecoration: "underline" }}>
                            Progressions Drill
                        </Link>{" "}
                        and begin training your ear for harmonic movement!
                    </p>
                </CalloutBox>
            </ContentSection>

            {/* Practice Strategies */}
            <ContentSection id="practice-strategies">
                <h2 className={styles.sectionTitle}>Effective practice strategies</h2>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>1. Master chord quality first</h3>
                <p>
                    Before diving deep into progressions, make sure you can reliably distinguish
                    major from minor chords. Progression recognition builds on this foundation. Use
                    the{" "}
                    <Link to={ROUTES.DRILL.CHORDS} style={{ color: "var(--accent-color)", textDecoration: "underline" }}>
                        Chords Drill
                    </Link>{" "}
                    to strengthen this skill.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>2. Start with three chords and easy difficulty</h3>
                <p>
                    Begin with 3-chord progressions using only I, IV, V, and vi. Use Easy
                    difficulty so the wrong answers are very different. Once you're hitting 80%+
                    accuracy, gradually add complexity.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>3. Use the reference note liberally</h3>
                <p>
                    The reference note button plays the tonic (I) of the key. Use it frequently to
                    keep your sense of the home chord strong. Over time, you'll need it less as
                    your relative pitch develops.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>4. Sing along with the progressions</h3>
                <p>
                    After hearing a progression, try to sing each chord's root note in sequence.
                    This helps you internalize the melodic shape of the bass line and strengthens
                    your connection to scale degrees.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>5. Practice in one key at a time</h3>
                <p>
                    Start by practicing progressions in just one key (like C Major) until you're
                    comfortable. Then switch keys to ensure you're recognizing relationships, not
                    just memorizing absolute pitches.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>6. Increase difficulty gradually</h3>
                <p>Follow this progression path:</p>
                <ol style={{ marginLeft: "1.5rem", lineHeight: "1.8" }}>
                    <li>3 chords (I, IV, V, vi only) on Easy difficulty</li>
                    <li>3 chords with all diatonic chords on Easy difficulty</li>
                    <li>3 chords on Medium difficulty</li>
                    <li>4 chords on Easy difficulty</li>
                    <li>4 chords on Medium difficulty</li>
                    <li>Finally, 4 chords on Hard difficulty</li>
                </ol>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>7. Apply to real songs</h3>
                <p>
                    The ultimate practice is analyzing songs you love. Try to figure out their
                    progressions by ear:
                </p>
                <ul style={{ marginLeft: "1.5rem", lineHeight: "1.8" }}>
                    <li>Find the tonic chord first (usually at the beginning or end)</li>
                    <li>Listen for the bass notes moving between chords</li>
                    <li>Identify if each chord is major or minor</li>
                    <li>Match what you hear to Roman numerals</li>
                </ul>
                <p>
                    Start with simple songs that use just 3-4 chords. Most pop and rock songs are
                    perfect for this!
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>8. Learn famous progressions by heart</h3>
                <p>
                    Memorize how the big progressions sound: I-V-vi-IV, I-IV-V, vi-IV-I-V, ii-V-I.
                    Play them on your instrument. Sing them. When these patterns are in your bones,
                    you'll recognize them instantly in the wild.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>9. Daily, consistent practice</h3>
                <p>
                    Even 5-10 minutes daily with the progressions drill will yield dramatic results
                    over weeks and months. Your brain needs regular exposure to build pattern
                    recognition. Make it a routine.
                </p>

                <CalloutBox>
                    <p>
                        <strong>Be patient:</strong> Progression recognition is one of the more
                        advanced ear training skills. It combines chord recognition, relative pitch,
                        and pattern memory. If it feels challenging at first, that's completely
                        normal! Stick with it, and one day it will suddenly click—you'll hear a
                        song and just <em>know</em> what the progression is.
                    </p>
                </CalloutBox>

                <div style={{ marginTop: "3rem", padding: "1.5rem", background: "var(--callout-bg)", borderRadius: "8px" }}>
                    <h3 style={{ marginTop: 0 }}>Next Steps</h3>
                    <p style={{ marginBottom: "1rem" }}>
                        You now have the foundation to understand progressions. Time to practice:
                    </p>
                    <Link
                        to={ROUTES.DRILL.PROGRESSIONS}
                        style={{
                            display: "inline-block",
                            padding: "0.75rem 1.5rem",
                            background: "var(--accent-color)",
                            color: "white",
                            borderRadius: "6px",
                            textDecoration: "none",
                            fontWeight: "600",
                            marginRight: "1rem",
                        }}
                    >
                        Start Progressions Drill →
                    </Link>
                    <p style={{ marginTop: "1.5rem", marginBottom: 0 }}>
                        Or review the fundamentals:{" "}
                        <Link to={ROUTES.LEARN.INTERVALS} style={{ color: "var(--accent-color)", textDecoration: "underline" }}>
                            Intervals
                        </Link>{" "}
                        and{" "}
                        <Link to={ROUTES.LEARN.CHORDS} style={{ color: "var(--accent-color)", textDecoration: "underline" }}>
                            Chords
                        </Link>
                        .
                    </p>
                </div>
            </ContentSection>
        </LearnPageLayout>
        </>
    );
}
